if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        /*
          A product video in the dialog is printed without a source (snippets/product-media.liquid).
          Pressing its poster puts the <video> in place (DeferredMedia in assets/global.js); this
          handler, which runs after that one, gives it the rendition the gallery would choose
          (window.afPdpVideoSource in assets/media-gallery.js: never 1080p) and starts it.
        */
        this.addEventListener('click', (event) => {
          const poster = event.target.closest('.deferred-media__poster');
          if (!poster || !poster.parentElement) return;
          const video = poster.parentElement.querySelector('video[data-af-zoom-video]');
          if (video) this.playVideo(video);
        });
        this.leaveByTouch();
      }

      /*
        Leaving the dialog on a touch screen (owner, 3 Oct 2026: on her phone the page "locked"
        after swiping the photographs; a swipe had opened this dialog, whose only way out by touch
        was the close button in the corner, and the page behind a dialog does not scroll).
        With a mouse a click anywhere closes it (ModalDialog in assets/global.js). By touch:
        - a tap on the photograph closes it (a drag pans the photograph, as before);
        - a drag that starts at the top of the photograph and pulls down, or starts at its bottom
          and pushes up, closes it: the shopper who is trying to scroll the page gets the page back.
        The close button and Escape work as before.
      */
      leaveByTouch() {
        let pointer = 'mouse';
        this.addEventListener(
          'pointerdown',
          (event) => {
            pointer = event.pointerType;
          },
          { passive: true }
        );
        this.addEventListener('click', (event) => {
          if (pointer === 'mouse' || event.detail === 0 || !this.hasAttribute('open')) return;
          if (event.target.closest('deferred-media, product-model, button, a, video')) return;
          this.hide();
        });

        const content = this.querySelector('[role="document"]');
        if (!content) return;
        let start = null;
        content.addEventListener(
          'touchstart',
          (event) => {
            if (event.touches.length !== 1) {
              start = null;
              return;
            }
            start = {
              x: event.touches[0].clientX,
              y: event.touches[0].clientY,
              atTop: content.scrollTop <= 1,
              atBottom: content.scrollTop + content.clientHeight >= content.scrollHeight - 1,
            };
          },
          { passive: true }
        );
        content.addEventListener(
          'touchend',
          (event) => {
            const from = start;
            start = null;
            if (!from || event.touches.length || !event.changedTouches.length || !this.hasAttribute('open')) return;
            const dx = event.changedTouches[0].clientX - from.x;
            const dy = event.changedTouches[0].clientY - from.y;
            const vertical = Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx) * 1.5;
            if (vertical && ((dy > 0 && from.atTop) || (dy < 0 && from.atBottom))) this.hide();
          },
          { passive: true }
        );
      }

      playVideo(video) {
        const start = () => {
          if (!this.hasAttribute('open')) return;
          const playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(() => {});
        };
        if (typeof window.afPdpVideoSource === 'function') {
          window
            .afPdpVideoSource(video)
            .then(start)
            .catch(() => {});
        } else if (video.dataset.mp4 || video.dataset.hls) {
          video.src = video.dataset.mp4 || video.dataset.hls;
          start();
        }
      }

      hide() {
        // a video started in the dialog stops with it (it kept streaming behind the page)
        this.querySelectorAll('video').forEach((video) => video.pause());
        super.hide();
        // gallery videos that were playing when the dialog opened carry on
        (this.pausedBehind || []).forEach((video) => {
          const playing = video.play();
          if (playing && typeof playing.catch === 'function') playing.catch(() => {});
        });
        this.pausedBehind = [];
      }

      show(opener) {
        // nothing plays behind the dialog: it covers the whole screen
        this.pausedBehind = Array.from(document.querySelectorAll('media-gallery video')).filter((video) => !video.paused);
        this.pausedBehind.forEach((video) => video.pause());
        super.show(opener);
        this.showActiveMedia();
      }

      /*
        The large photograph is fetched when the dialog opens (it is lazy in the markup, so a page
        view costs nothing for it). Until it arrives the dialog showed a blank white screen with a
        close button; now the photograph the shopper tapped, already on the page, stands in for it.
      */
      showAtOnce(media) {
        if (!media || media.tagName !== 'IMG') return;
        media.loading = 'eager';
        if (media.complete && media.naturalWidth) return;
        const tile = this.openedBy && this.openedBy.closest('.product__media-item');
        const shown = tile && tile.querySelector('.product__media img');
        const url = shown && (shown.currentSrc || shown.src);
        if (!url) return;
        media.style.background = `center / 100% 100% no-repeat url("${url}")`;
        media.addEventListener(
          'load',
          () => {
            media.style.background = '';
          },
          { once: true }
        );
      }

      showActiveMedia() {
        this.querySelectorAll(
          `[data-media-id]:not([data-media-id="${this.openedBy.getAttribute('data-media-id')}"])`
        ).forEach((element) => {
          element.classList.remove('active');
        });
        const activeMedia = this.querySelector(`[data-media-id="${this.openedBy.getAttribute('data-media-id')}"]`);
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        this.showAtOnce(activeMedia);
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }
    }
  );
}
